import React, { useState, useEffect, useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { X, Loader2 } from 'lucide-react';

import { ProductTable } from '../../../activos/productos/components/ProductTable';

import { productService } from '../../../../services/productos/productService';

const DEFAULT_PAGE_SIZE = 5;

const PAGE_SIZE_OPTIONS = [5, 10];

 

export const ProductPickerModal = ({

  isOpen,

  onClose,

  onSelect,

  initialProductId = null,

}) => {

  const [searchQuery, setSearchQuery] = useState('');

  const [filterProductType, setFilterProductType] = useState('');

  const [filterManufacturer, setFilterManufacturer] = useState('');

  const [filterFamily, setFilterFamily] = useState('');

  const [page, setPage] = useState(1);

  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [pendingProduct, setPendingProduct] = useState(null);

 

  const {

    data: products = [],

    isLoading,

    isError,

    error,

  } = useQuery({

    queryKey: ['products'],

    queryFn: () => productService.getProducts(),

    enabled: isOpen,

    retry: 1,

  });

 

  useEffect(() => {

    if (isOpen) {

      setSearchQuery('');

      setFilterProductType('');

      setFilterManufacturer('');

      setFilterFamily('');

      setPage(1);

      setPageSize(DEFAULT_PAGE_SIZE);

      if (initialProductId != null) {

        const match = products.find((p) => p.id === initialProductId);

        setPendingProduct(match ?? null);

      } else {

        setPendingProduct(null);

      }

    }

  }, [isOpen, initialProductId, products]);

 

  const uniqueProductTypes = useMemo(

    () => [...new Set(products.map((p) => p.product_type || p.ProductType).filter(Boolean))].sort(),

    [products]

  );

 

  const uniqueManufacturers = useMemo(

    () => [...new Set(products.map((p) => p.manufacturer || p.Manufacturer).filter(Boolean))].sort(),

    [products]

  );

 

  const uniqueFamilies = useMemo(

    () => [...new Set(products.map((p) => p.family || p.Family).filter(Boolean))].sort(),

    [products]

  );

 

  const filteredProducts = useMemo(() => {

    let filtered = products;

 

    if (searchQuery) {

      const query = searchQuery.toLowerCase();

      filtered = filtered.filter(

        (product) =>

          (product.name || product.Name || '').toLowerCase().includes(query) ||

          (product.product_type || product.ProductType || '').toLowerCase().includes(query) ||

          (product.manufacturer || product.Manufacturer || '').toLowerCase().includes(query) ||

          (product.family || product.Family || '').toLowerCase().includes(query) ||

          (product.description || product.Description || '').toLowerCase().includes(query)

      );

    }

 

    if (filterProductType) {

      filtered = filtered.filter(

        (product) => (product.product_type || product.ProductType) === filterProductType

      );

    }

 

    if (filterManufacturer) {

      filtered = filtered.filter(

        (product) => (product.manufacturer || product.Manufacturer) === filterManufacturer

      );

    }

 

    if (filterFamily) {

      filtered = filtered.filter(

        (product) => (product.family || product.Family) === filterFamily

      );

    }

 

    return filtered;

  }, [products, searchQuery, filterProductType, filterManufacturer, filterFamily]);

 

  const totalItems = filteredProducts.length;

  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  const paginatedProducts = useMemo(

    () => filteredProducts.slice((page - 1) * pageSize, page * pageSize),

    [filteredProducts, page, pageSize]

  );

  const rangeStart = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;

  const rangeEnd = Math.min(page * pageSize, totalItems);

 

  useEffect(() => {

    setPage(1);

  }, [searchQuery, filterProductType, filterManufacturer, filterFamily, pageSize]);

 

  const handleProductClick = (product) => {

    setPendingProduct(product);

  };

 

  const handleConfirm = () => {

    if (pendingProduct?.id != null) {

      onSelect(pendingProduct);

      onClose();

    }

  };

 

  if (!isOpen) return null;

 

  return (

    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">

      <div className="bg-gray-900 rounded-2xl border border-gray-700 shadow-xl w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col">

        <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between shrink-0">

          <h2 className="text-2xl font-semibold text-white">Seleccionar producto</h2>

          <button

            type="button"

            onClick={onClose}

            className="p-2 rounded-lg hover:bg-gray-700 text-gray-400 hover:text-white transition-colors"

          >

            <X className="w-5 h-5" />

          </button>

        </div>

 

        <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6">

          {isLoading ? (

            <div className="flex items-center justify-center py-16">

              <Loader2 className="w-10 h-10 text-blue-500 animate-spin" />

            </div>

          ) : isError ? (

            <div className="text-center py-16">

              <p className="text-red-400 mb-2">Error al cargar productos</p>

              <p className="text-gray-400 text-sm">{error?.message || 'Error desconocido'}</p>

            </div>

          ) : (

            <ProductTable

              products={paginatedProducts}

              totalItems={totalItems}

              totalPages={totalPages}

              page={page}

              setPage={setPage}

              pageSize={pageSize}

              setPageSize={setPageSize}

              pageSizeOptions={PAGE_SIZE_OPTIONS}

              rangeStart={rangeStart}

              rangeEnd={rangeEnd}

              searchQuery={searchQuery}

              setSearchQuery={setSearchQuery}

              filterProductType={filterProductType}

              setFilterProductType={setFilterProductType}

              filterManufacturer={filterManufacturer}

              setFilterManufacturer={setFilterManufacturer}

              filterFamily={filterFamily}

              setFilterFamily={setFilterFamily}

              uniqueProductTypes={uniqueProductTypes}

              uniqueManufacturers={uniqueManufacturers}

              uniqueFamilies={uniqueFamilies}

              onProductClick={handleProductClick}

              selectedProductId={pendingProduct?.id ?? null}

              showCreate={false}

              title="Productos disponibles"

            />

          )}

        </div>

 

        <div className="px-6 py-4 border-t border-gray-700 flex items-center justify-end gap-3 shrink-0">

          <button

            type="button"

            onClick={onClose}

            className="px-4 py-2 rounded-lg border border-gray-600 bg-gray-800 text-gray-300 text-sm font-medium hover:bg-gray-700 transition-colors"

          >

            Cancelar

          </button>

          <button

            type="button"

            onClick={handleConfirm}

            disabled={pendingProduct?.id == null}

            className="px-4 py-2 rounded-lg border border-blue-600 bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"

          >

            Seleccionar

          </button>

        </div>

      </div>

    </div>

  );

};

 